import React, { useCallback } from 'react';
import { Stack, Text } from '@moda/om';
import WarningIcon from '@moda/icons/warning-16';
import { PaymentSource } from '../../../constants';
import { reportError } from '../../../lib/reportError';
import { useErrorModal } from '../../../hooks/useErrorModal';
import { useCheckout } from '../../../hooks/useCheckout';
import {
  useCart,
  useUpdateCartMutation,
  CartUpdateAction,
  CartUpdatePropertyName
} from '../../../hooks/useCart';
import { CheckoutSection } from '../../CheckoutSection';
import { useCheckoutFormStates } from '../../CheckoutFormStates';
import { AmazonShippingAndPayment, Mode } from '../AmazonShippingAndPayment';

export const CheckoutAmazonShippingAndPayment: React.FC = () => {
  const { showErrorModal } = useErrorModal();
  const {
    isShippingAddressSelected,
    isPaymentMethodSelected,
    isNonUSAmazonShippingAddressSelected
  } = useCheckout();
  const {
    cart: {
      attributes: { addressId }
    }
  } = useCart();
  const [updateCart] = useUpdateCartMutation();
  const { setFormState } = useCheckoutFormStates();

  const onSwitchToCard = useCallback(async () => {
    try {
      await updateCart(CartUpdateAction.PaymentSource, {
        [CartUpdatePropertyName.PaymentSource]: PaymentSource.Braintree
      });
    } catch (error) {
      reportError('CHECKOUT_AMAZON_PAY_SWITCH_TO_CARD', error);
      showErrorModal();
    }
  }, [showErrorModal, updateCart]);

  const onModeChange = useCallback(
    (mode: Mode) => {
      setFormState('shippingAddress', mode === Mode.Resting ? 'closed' : 'open');
      setFormState('paymentMethod', mode === Mode.Resting ? 'closed' : 'open');
    },
    [setFormState]
  );

  return (
    <CheckoutSection title="Shipping &amp; Payment">
      <Stack space={3}>
        <AmazonShippingAndPayment
          addressId={addressId}
          isFilled={isShippingAddressSelected && isPaymentMethodSelected}
          onSwitchToCard={onSwitchToCard}
          onModeChange={onModeChange}
        />
        {isNonUSAmazonShippingAddressSelected && (
          <Stack direction="horizontal" space={1} alignItems="center">
            <Text color="code-red">
              <WarningIcon />
            </Text>
            <Text color="code-red">
              International shipping is not supported through Amazon Pay. Please select a US
              shipping address, or switch to another payment method.
            </Text>
          </Stack>
        )}
      </Stack>
    </CheckoutSection>
  );
};
